/**
 * Notifications (OnlyFans /my/notifications) — the CRM page's data rules
 * (crm/src/public/notifications/notifications.js), kept pure: tab → endpoint
 * mapping, request bodies, response unwrapping, paging, feed merging, the
 * fan behind a row, the local read overlay and the tab badge counts.
 */
import { errorMessage } from '../../chat/api'

export type Raw = Record<string, unknown>

export const PAGE_LIMIT = 50
export const USERS_LIST_CHUNK = 100
export const MARK_READ_BATCH = 100

// ── Tabs ────────────────────────────────────────────────────────────────────

export type TabId = 'all' | 'subscribe' | 'purchases' | 'tip' | 'tags' | 'comment' | 'mention'

export interface TabConfig {
  /** `notifications` pages by cursor; `mentions` (release forms) by offset. */
  source: 'notifications' | 'mentions'
  /** The gateway's `type` filter. */
  apiType: string | null
}

export const TAB_CONFIG: Record<TabId, TabConfig> = {
  all: { source: 'notifications', apiType: null },
  subscribe: { source: 'notifications', apiType: 'subscribed' },
  purchases: { source: 'notifications', apiType: 'purchases' },
  tip: { source: 'notifications', apiType: 'tip' },
  tags: { source: 'mentions', apiType: null },
  comment: { source: 'notifications', apiType: 'commented' },
  mention: { source: 'notifications', apiType: 'mentioned' },
}

export const TABS: TabId[] = ['all', 'subscribe', 'purchases', 'tip', 'tags', 'comment', 'mention']

export const isTab = (v: unknown): v is TabId => typeof v === 'string' && (TABS as string[]).includes(v)

export const NOTIFICATIONS_PATH = '/v1/users/notifications'
export const MENTIONS_PATH = '/v1/release-forms/mentions'
export const COUNT_PATH = '/v1/users/notifications/count'
export const TABS_ORDER_PATH = '/v1/users/notifications/tabs-order'
export const MARK_READ_PATH = '/v1/users/notifications/mark-read'
export const USERS_LIST_PATH = '/v1/users/list'

export const endpointFor = (tab: TabId) => (TAB_CONFIG[tab].source === 'mentions' ? MENTIONS_PATH : NOTIFICATIONS_PATH)

/** The request body the CRM sends for a page of a tab. */
export function requestBody(
  tab: TabId,
  { accountId, limit = PAGE_LIMIT, offset = 0, cursor = null }: { accountId: string; limit?: number; offset?: number; cursor?: string | null },
): Raw {
  const cfg = TAB_CONFIG[tab]
  if (cfg.source === 'mentions') return { account_id: accountId, limit, offset }
  const body: Raw = { account_id: accountId, limit, format: 'infinite' }
  if (cfg.apiType) body.type = cfg.apiType
  if (cursor) {
    // Every cursor key the gateway knows (as chats and chat messages send it).
    body.marker = cursor
    body.marker_id = cursor
    body.marker_message_id = cursor
    body.from_id = cursor
    body.fromId = cursor
  }
  return body
}

const ALIASES: Record<string, TabId> = {
  subscribed: 'subscribe',
  subscribe: 'subscribe',
  commented: 'comment',
  comment: 'comment',
  mentioned: 'mention',
  mention: 'mention',
  mentions: 'tags',
  tags: 'tags',
  purchase: 'purchases',
  purchases: 'purchases',
  tips: 'tip',
  tip: 'tip',
  all: 'all',
}

/** `tabs-order` answer → the tab order (unknown entries dropped, missing tabs appended). */
export function tabsOrder(data: unknown): TabId[] | null {
  const d = obj(data)
  const payload = d.data ?? d
  const p = obj(payload)
  const order = p.order ?? p.tabs ?? p.list ?? (Array.isArray(payload) ? payload : [])
  if (!Array.isArray(order) || !order.length) return null
  const out: TabId[] = []
  for (const item of order) {
    const it = obj(item)
    const raw = String(it.id ?? it.type ?? it.tab ?? it.key ?? (typeof item === 'string' ? item : '')).toLowerCase()
    const id = ALIASES[raw] ?? (isTab(raw) ? raw : null)
    if (id && !out.includes(id)) out.push(id)
  }
  for (const t of TABS) if (!out.includes(t)) out.push(t)
  return out
}

// ── Gateway envelope ────────────────────────────────────────────────────────

const GENERIC_ERROR = 'Something went wrong. Please try again.'
const ERROR_MAP: [RegExp, string][] = [
  [/cloudflare/i, 'Service is temporarily unavailable. Try again later.'],
  [/request failed/i, GENERIC_ERROR],
  [/failed to fetch/i, 'Network error. Check your connection and try again.'],
  [/network error/i, 'Network error. Check your connection and try again.'],
]

/** CRM ui.js formatUserFacingError: no correlation ids, status prefixes or raw JSON. */
export function userFacingError(raw: unknown, fallback = GENERIC_ERROR): string {
  let s = String(raw ?? '').trim()
  if (!s) return fallback
  s = s
    .replace(/\s*\[[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\]/gi, '')
    .replace(/^Gateway\s*\d+\s*:\s*/i, '')
    .replace(/^(HTTP|Error)\s*\d+\s*:?\s*/i, '')
    .trim()
  for (const [re, text] of ERROR_MAP) if (re.test(s)) return text
  if (/^Gateway\s/i.test(s) || /^HTTP\s*\d/i.test(s) || /^[0-9a-f-]{36}$/i.test(s)) return fallback
  if (s.length > 140 || /^\{.*\}$/.test(s) || s.includes('"ok"')) return fallback
  return s
}

/** CRM extractGatewayError: a message when the gateway envelope says `ok: false`. */
export function gatewayError(data: unknown): string | null {
  const d = obj(data)
  if (d.ok !== false) return null
  if (typeof d.data === 'string' && d.data.includes('Cloudflare')) return userFacingError('blocked by Cloudflare upstream')
  const p = obj(d.data)
  const tail =
    errorMessage(data, '') ||
    (typeof p.error === 'string' ? p.error : '') ||
    (typeof d.operation === 'string' ? d.operation : '') ||
    'request failed'
  return userFacingError(tail)
}

/** What a failed list load shows (CRM renderError). */
export function loadError(data: unknown): string {
  const d = obj(data)
  const raw = (d.ok === false ? gatewayError(data) : '') || [d.detail, d.message, d.error].find((v) => typeof v === 'string' && v) || ''
  return userFacingError(raw)
}

// ── List payload ────────────────────────────────────────────────────────────

const hasList = (o: Raw) => Array.isArray(o.list) || Array.isArray(o.notifications) || Array.isArray(o.items)

/** The gateway sometimes nests list/hasMore one level deeper (data.data). */
export function unwrapPayload(data: unknown): { payload: Raw; parent: Raw } {
  const d = obj(data)
  const l1 = d.data ?? d
  if (!l1 || typeof l1 !== 'object') return { payload: {}, parent: {} }
  const L1 = l1 as Raw
  if (hasList(L1)) return { payload: L1, parent: {} }
  const L2 = L1.data ?? L1.result
  if (L2 && typeof L2 === 'object' && hasList(L2 as Raw)) return { payload: L2 as Raw, parent: L1 }
  return { payload: L1, parent: {} }
}

export function extractList(data: unknown): Raw[] {
  const { payload } = unwrapPayload(data)
  const d = obj(data)
  const raw = payload.list ?? payload.notifications ?? payload.items ?? (Array.isArray(d.data) ? d.data : [])
  return Array.isArray(raw) ? raw.filter((n): n is Raw => !!n && typeof n === 'object') : []
}

function coerceBool(v: unknown): boolean | null {
  if (v === true || v === 1 || v === '1') return true
  if (v === false || v === 0 || v === '0') return false
  if (typeof v === 'string' && v.toLowerCase() === 'true') return true
  if (typeof v === 'string' && v.toLowerCase() === 'false') return false
  return null
}

const rowId = (n: Raw | undefined) => {
  const id = n?.id ?? n?.notification_id ?? n?.message_id
  return id != null && String(id).trim() !== '' ? String(id) : null
}

export function extractPaging(data: unknown, useOffset: boolean, list: Raw[], limit = PAGE_LIMIT): { hasMore: boolean; marker: string | null } {
  const { payload: p, parent } = unwrapPayload(data)
  const d = obj(data)
  const explicit = coerceBool(
    p.hasMore ?? p.has_more ?? p.more ?? p.canLoadMore ?? p.can_load_more ?? p.hasNext ?? p.has_next ?? parent.hasMore ?? parent.has_more ?? d.hasMore ?? d.has_more,
  )
  const hasMore = explicit === true || (explicit !== false && list.length >= Math.max(1, limit))
  if (useOffset) return { hasMore, marker: null }
  let marker =
    p.fromId ?? p.from_id ?? p.nextFromId ?? p.next_from_id ?? p.marker ?? p.marker_id ?? p.next_marker ?? p.nextMarker ?? parent.fromId ?? parent.from_id ?? parent.marker ?? parent.marker_id ?? parent.next_marker ?? null
  if ((marker == null || String(marker).trim() === '') && list.length) marker = rowId(list[list.length - 1])
  return { hasMore, marker: marker != null && String(marker).trim() !== '' ? String(marker) : null }
}

/** The oldest loaded row's id: the cursor for the next page. */
export const cursorFromList = (list: Raw[]) => (list.length ? rowId(list[list.length - 1]) : null)

/**
 * Merges a page into the newest-first feed: rows before the first overlap are
 * newer (prepended), rows after it older (appended); no overlap appends all.
 */
export function mergeFeed(existing: Raw[], batch: Raw[]) {
  const seen = new Set(existing.map((n) => String(n.id ?? '')).filter(Boolean))
  const prepend: Raw[] = []
  const append: Raw[] = []
  let overlap = false
  for (const n of batch) {
    const id = String(n.id ?? '')
    if (!id) continue
    if (seen.has(id)) {
      overlap = true
      continue
    }
    seen.add(id)
    ;(overlap ? append : prepend).push(n)
  }
  if (!overlap && batch.length) {
    return { list: [...existing, ...prepend], prependCount: 0, appendCount: prepend.length, newCount: prepend.length }
  }
  return { list: [...prepend, ...existing, ...append], prependCount: prepend.length, appendCount: append.length, newCount: prepend.length + append.length }
}

export type Stuck = 'duplicate' | 'overlap' | null

export interface FeedState {
  rows: Raw[]
  hasMore: boolean
  cursor: string | null
  offset: number
  stuck: Stuck
}

export const EMPTY_FEED: FeedState = { rows: [], hasMore: true, cursor: null, offset: 0, stuck: null }

/** The CRM's paging rules after a page arrives (reset = first page). */
export function applyPage(prev: FeedState, tab: TabId, data: unknown, batch: Raw[], reset: boolean, limit = PAGE_LIMIT): FeedState & { prependCount: number } {
  const useOffset = TAB_CONFIG[tab].source === 'mentions'
  const merged = reset ? { list: batch, prependCount: 0, appendCount: batch.length, newCount: batch.length } : mergeFeed(prev.rows, batch)
  const rows = merged.list
  const requestCursor = reset ? null : prev.cursor
  const paging = extractPaging(data, useOffset, batch, limit)
  const base = { rows, prependCount: merged.prependCount, offset: reset ? 0 : prev.offset }
  if (!reset && batch.length === 0) return { ...base, hasMore: false, cursor: null, stuck: null }
  if (!reset && merged.newCount === 0) return { ...base, hasMore: false, cursor: null, stuck: 'duplicate' }
  if (!reset && requestCursor && merged.appendCount === 0 && merged.prependCount > 0) return { ...base, hasMore: false, cursor: null, stuck: 'overlap' }
  if (useOffset) return { ...base, offset: base.offset + batch.length, hasMore: paging.hasMore && batch.length > 0, cursor: null, stuck: null }
  const cursor = cursorFromList(rows) ?? paging.marker ?? (reset ? null : prev.cursor)
  let hasMore = paging.hasMore && batch.length > 0
  if (!reset && requestCursor && String(cursor ?? '') === String(requestCursor)) hasMore = false
  return { ...base, cursor, hasMore, stuck: null }
}

// ── Users ───────────────────────────────────────────────────────────────────

export interface CachedUser {
  name: string
  username: string
  avatar: string
  displayName?: string
  profile_name?: string
}
export type UserCache = Record<string, CachedUser>

const str = (v: unknown) => (v == null ? '' : String(v).trim())

export const isPlaceholderName = (raw: unknown) => {
  const s = str(raw)
  return !s || /^(fan|user)\s*#\s*\d+$/i.test(s) || /^@\s*fan_\d+$/i.test(s)
}

export function pickName(...candidates: unknown[]): string {
  for (const c of candidates) {
    const s = str(c)
    if (s && !isPlaceholderName(s)) return s
  }
  return ''
}

export function pickAvatar(user: unknown): string {
  const u = obj(user)
  const th = obj(u.avatarThumbs ?? u.avatar_thumbs)
  return str(th.c144 || th.c50 || th.medium || th.thumb || th.full || u.avatar || u.avatar_url || u.avatarUrl)
}

/** CRM setCachedUserProfile: one users/list (or embedded) user into the cache. */
export function cacheUser(user: Raw, prev?: CachedUser): CachedUser {
  const raw = str(user.displayName || user.profile_name || user.name || user.username)
  const name = raw && !isPlaceholderName(raw) ? raw : str(user.username)
  const out: CachedUser = {
    name: name || prev?.name || '',
    username: str(user.username || user.userName) || prev?.username || '',
    avatar: pickAvatar(user) || prev?.avatar || '',
  }
  const disp = str(user.displayName) || prev?.displayName
  const prof = str(user.profile_name) || prev?.profile_name
  if (disp) out.displayName = disp
  if (prof) out.profile_name = prof
  return out
}

/** OF users/list: an array, { list }, { users } or a map { "id": user }. */
export function normalizeUsersList(raw: unknown): Raw[] {
  if (!raw) return []
  if (Array.isArray(raw)) return raw.filter((u): u is Raw => !!u && typeof u === 'object')
  if (typeof raw !== 'object') return []
  const r = raw as Raw
  if (Array.isArray(r.list)) return normalizeUsersList(r.list)
  if (Array.isArray(r.users)) return normalizeUsersList(r.users)
  const keys = Object.keys(r)
  if (!keys.length) return []
  const s = obj(r[keys[0]!])
  const looksLikeMap = r[keys[0]!] && typeof r[keys[0]!] === 'object' && (s.id != null || s.name != null || s.username != null || s.userName != null || s.avatar != null || s.avatarThumbs != null)
  if (!looksLikeMap) return []
  const out: Raw[] = []
  for (const k of keys) {
    const u = r[k]
    if (!u || typeof u !== 'object') continue
    const uu = u as Raw
    const id = str(uu.id ?? uu.user_id ?? uu.fan_id ?? k)
    if (id) out.push({ ...uu, id })
  }
  return out
}

/** users/list answer → users. */
export function usersFromList(data: unknown): Raw[] {
  const d = obj(data)
  const payload = obj(d.data ?? d)
  const raw = payload.list ?? payload.users ?? (payload.data && typeof payload.data === 'object' ? payload.data : null) ?? payload
  const users = normalizeUsersList(raw)
  return users.length ? users : normalizeUsersList(payload)
}

export const userId = (u: Raw) => str(u.id || u.user_id || u.fan_id)

export const needsUser = (cache: UserCache, id: string) => {
  const c = cache[id]
  return !!id && (!c || !pickName(c.displayName, c.profile_name, c.name, c.username) || !c.avatar)
}

// ── One notification ────────────────────────────────────────────────────────

export function stripHtml(html: unknown): string {
  if (!html) return ''
  return String(html)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .trim()
}

export function replacePairs(n: Raw): Raw {
  const rp = n.replacePairs ?? n.replace_pairs
  if (!rp) return {}
  if (Array.isArray(rp)) {
    const out: Raw = {}
    for (const item of rp) {
      if (!item || typeof item !== 'object') continue
      const it = item as Raw
      const k = it.key ?? it.name ?? it.field ?? it.placeholder
      const v = it.value ?? it.val ?? it.text ?? it.content
      if (k != null && v != null) out[String(k)] = v
    }
    return out
  }
  return typeof rp === 'object' ? (rp as Raw) : {}
}

function subscriberNameFromPairs(rp: Raw): string {
  const raw = str(rp.SUBSCRIBER_LINK || rp['{SUBSCRIBER_LINK}'] || rp.subscriber_link)
  const m = raw.match(/>([^<]+)</)
  return m?.[1] ? m[1].replace(/\s+-\s+active\s+now.*$/i, '').trim() : ''
}

/** The fan's name from replacePairs or the notification text. */
export function parseFanName(n: Raw): string {
  const rp = replacePairs(n)
  const fromLink = subscriberNameFromPairs(rp)
  if (fromLink) return fromLink
  const fromPairs = pickName(rp.name, rp.userName, rp.user_name, rp.displayName, rp.display_name, rp.profile_name, rp.subscriberName, rp.subscriber_name, rp.username)
  if (fromPairs) return fromPairs
  const text = stripHtml(n.text || n.message || n.description || rp.text || '')
  if (!text) return ''
  let m = text.match(/Good news!\s*(.+?)\s+has just restarted/i)
  if (m?.[1]) return m[1].trim()
  m = text.match(/subscription to\s+(.+?)\s+has expired/i)
  if (m?.[1]) return m[1].replace(/[💗♥❤]/gu, '').trim()
  m = text.match(/^([A-Za-z][\w\s.'-]{1,48}?)\s+(?:has|have)\s+/i)
  if (m?.[1] && !/^(good|your|the|a)\s/i.test(m[1])) return m[1].trim()
  return ''
}

/** Every embedded user-ish object of a notification. */
export function userEntities(n: Raw): Raw[] {
  const rp = replacePairs(n)
  return [n.user, n.fromUser, n.from_user, n.subscribedOn, n.subscribed_on, n.subscribedBy, n.subscribed_by, n.subscriber, n.sender, rp.user, rp].filter(
    (u): u is Raw => !!u && typeof u === 'object' && !Array.isArray(u),
  )
}

function mainEntity(n: Raw): Raw {
  const all = userEntities(n)
  return all.find((u) => u.id || u.username || u.userName || u.name || u.displayName) ?? all[0] ?? {}
}

function fanIdFromHandle(n: Raw): string {
  const rp = replacePairs(n)
  const e = mainEntity(n)
  const u = obj(n.user)
  const f = obj(n.fromUser)
  const fu = obj(n.from_user)
  for (const raw of [e.username, e.userName, u.username, f.username, fu.username, rp.USERNAME, rp['{USERNAME}'], rp.username, rp.userName]) {
    const m = str(raw).match(/^@?u(\d{3,})$/i)
    if (m?.[1]) return m[1]
  }
  return ''
}

/** The fan behind a notification (opens their chat). */
export function fanIdOf(n: Raw): string {
  const rp = replacePairs(n)
  const id = (k: string) => obj(n[k]).id
  return str(
    n.withUserId ||
      id('withUser') ||
      id('user') ||
      id('fromUser') ||
      id('from_user') ||
      n.fan_id ||
      n.user_id ||
      id('subscribedBy') ||
      id('subscribed_by') ||
      id('relatedUser') ||
      id('related_user') ||
      n.userId ||
      n.subscriberId ||
      n.subscriber_id ||
      n.relatedUserId ||
      n.related_user_id ||
      rp.USER_ID ||
      rp['{USER_ID}'] ||
      rp.userId ||
      rp.user_id ||
      rp.fan_id ||
      rp.id ||
      fanIdFromHandle(n),
  )
}

/** CRM seedCacheFromNotifications: what the rows themselves say about their fans. */
export function seedUsers(rows: Raw[], cache: UserCache): UserCache {
  const out: UserCache = {}
  for (const n of rows) {
    const uid = fanIdOf(n)
    if (!uid) continue
    const rp = replacePairs(n)
    const entities = userEntities(n)
    let name = subscriberNameFromPairs(rp) || parseFanName(n)
    let username = ''
    let avatar = ''
    for (const e of entities) {
      name = name || pickName(e.displayName, e.profile_name, e.name, rp.name, rp.displayName)
      username = username || str(e.username || e.userName || rp.username)
      avatar = avatar || pickAvatar(e)
    }
    if (!name && !username && !avatar) continue
    const first = entities[0] ?? {}
    out[uid] = cacheUser(
      { ...first, ...rp, id: uid, name: name || first.name, displayName: first.displayName || rp.displayName || name, profile_name: first.profile_name || rp.profile_name, username, avatar },
      out[uid] ?? cache[uid],
    )
  }
  return out
}

export interface NotifAction {
  /** Server text (HTML stripped). */
  text?: string
  /** Else an i18n key under `action.` with an optional amount. */
  key?: string
  amount?: string
}

const DEFAULT_ACTION: Record<string, string> = {
  subscribed: 'subscribed',
  subscribe: 'subscribed',
  purchases: 'purchase',
  purchase: 'purchase',
  tip: 'tip',
  commented: 'comment',
  comment: 'comment',
  mentioned: 'mention',
  mention: 'mention',
  like: 'like',
  liked: 'like',
  message: 'message',
  stream: 'stream',
  expiration: 'expiring',
  expired: 'expired',
}

export function actionOf(n: Raw): NotifAction {
  const text = stripHtml(n.text || n.message || n.description || obj(n.replacePairs).text || '')
  if (text) return { text }
  const type = str(n.type || n.action || n.subType).toLowerCase()
  const amount = str(n.price || n.amount || n.tipAmount || n.tip_amount)
  const key = DEFAULT_ACTION[type]
  if (key === 'tip' && amount) return { key: 'tipAmount', amount }
  if (key) return { key }
  return type ? { text: type } : {}
}

/** "Lists • A • B" (or the subscription duration). */
export function listsOf(n: Raw): string[] {
  const lists = n.userLists ?? n.user_lists ?? n.listsStates ?? n.lists_states ?? []
  const parts: string[] = []
  if (Array.isArray(lists)) {
    for (const item of lists.slice(0, 4)) {
      const it = obj(item)
      const label = str(it.name || it.title || it.listName || it.list_name)
      if (label) parts.push(label)
    }
  }
  if (!parts.length && n.subscribedOnDuration) parts.push(str(n.subscribedOnDuration))
  return parts
}

/** The re-subscribe call to action of an expired/expiring subscription. */
export function ctaOf(n: Raw): { price: string } | null {
  const type = str(n.type || n.action).toLowerCase()
  if (!/expir|renew|resub/i.test(type)) return null
  return { price: str(n.renewPrice || n.renew_price || n.subscribePrice || n.subscribe_price) }
}

export const isUnread = (n: Raw) => !n.isRead && !n.is_read
export const createdAt = (n: Raw) => str(n.createdAt || n.created_at || n.timestamp)
export const isVerified = (n: Raw) => {
  const e = mainEntity(n)
  const u = obj(n.user)
  return Boolean(e.isVerified || e.is_verified || u.isVerified || u.is_verified)
}

/** How a row shows its fan: the users cache first (users/list), then the row. */
export function fanView(n: Raw, cache: UserCache) {
  const uid = fanIdOf(n)
  const c = uid ? cache[uid] : undefined
  const e = mainEntity(n)
  const u = obj(n.user)
  const rp = replacePairs(n)
  const entities = userEntities(n)
  const merged = uid ? pickName(c?.displayName, c?.profile_name, c?.name, e.displayName, e.profile_name, e.name, parseFanName(n), u.displayName, u.name) || c?.username || '' : ''
  const fromEntities = entities.flatMap((x) => [x.displayName, x.profile_name, x.name, x.username])
  const name = pickName(merged, ...fromEntities, n.userName, rp.name, rp.displayName, rp.username, parseFanName(n)) // '' → "User #id" (localised by the page)
  const handleRaw = str(c?.username || e.username || e.userName || u.username || obj(n.fromUser).username)
  const handle = handleRaw ? (handleRaw.startsWith('@') ? handleRaw : `@${handleRaw}`) : ''
  let avatar = str(c?.avatar)
  if (!avatar) for (const x of entities) if ((avatar = pickAvatar(x))) break
  return { fanId: uid, name, handle, avatar, verified: isVerified(n) }
}

// ── Time ────────────────────────────────────────────────────────────────────

export type TimeLabel = { key: 'justNow' } | { key: 'minutes' | 'hours'; count: number } | { date: string }

/** CRM formatNotifRelativeTime: just now / N minutes / N hours (same day) / "9 Oct". */
export function timeLabel(iso: string, lang: string, now = Date.now()): TimeLabel | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const min = Math.floor(Math.max(0, now - d.getTime()) / 60000)
  if (min < 1) return { key: 'justNow' }
  if (min < 60) return { key: 'minutes', count: min }
  const h = Math.floor(min / 60)
  const today = new Date(now)
  const sameDay = d.getDate() === today.getDate() && d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear()
  if (sameDay && h < 24) return { key: 'hours', count: h }
  const sameYear = d.getFullYear() === today.getFullYear()
  const date = d
    .toLocaleDateString(lang, { day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) })
    .replace(/\s*[gг]\./, '')
    .replace(/\./g, '')
    .trim()
  return { date }
}

// ── Read state (CRM-local: the gateway keeps reporting OnlyFans' own flag) ─

const READ_KEY = 'notifications_read_v1'
const READ_META_KEY = 'notifications_read_meta_v1'
const READ_MAX = 2000

type ReadMeta = Record<string, { type?: string; ts?: number }>

function readJson(key: string): unknown {
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}
function writeJson(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Private mode / full storage: read state lasts this visit.
  }
}

/** The ids this browser has marked read (the CRM's own keys, so both pages agree). */
export function readIds(accountId: string): Set<string> {
  const out = new Set<string>()
  for (const key of [READ_KEY, `${READ_KEY}_${accountId || 'default'}`]) {
    const arr = readJson(key)
    if (Array.isArray(arr)) for (const id of arr) if (str(id)) out.add(str(id))
  }
  return out
}

function writeIds(accountId: string, ids: Set<string>) {
  const list = [...ids].map(str).filter(Boolean).slice(-READ_MAX)
  writeJson(`${READ_KEY}_${accountId || 'default'}`, list)
  writeJson(READ_KEY, list)
}

export function readMeta(accountId: string): ReadMeta {
  const out: ReadMeta = {}
  for (const key of [READ_META_KEY, `${READ_META_KEY}_${accountId || 'default'}`]) {
    const m = readJson(key)
    if (m && typeof m === 'object' && !Array.isArray(m)) Object.assign(out, m)
  }
  return out
}

function writeMeta(accountId: string, meta: ReadMeta) {
  const next: ReadMeta = {}
  for (const [id, row] of Object.entries(meta).filter(([id]) => str(id)).slice(-READ_MAX)) next[id] = row && typeof row === 'object' ? row : {}
  writeJson(`${READ_META_KEY}_${accountId || 'default'}`, next)
  writeJson(READ_META_KEY, next)
}

const typeOf = (n: Raw) => str(n.type || n.action).toLowerCase()

/** Remembers rows as read (with their type, for the badge counts). */
export function rememberRead(accountId: string, rows: Raw[]) {
  const ids = readIds(accountId)
  const meta = readMeta(accountId)
  let any = false
  for (const row of rows) {
    const id = str(row.id)
    if (!id) continue
    any = true
    ids.add(id)
    meta[id] = { type: typeOf(row), ts: Date.now() }
  }
  if (!any) return
  writeIds(accountId, ids)
  writeMeta(accountId, meta)
}

/** Forgets ids the server now reports read, and fills in missing types. */
export function syncReadStore(accountId: string, rows: Raw[]) {
  if (!rows.length) return
  const ids = readIds(accountId)
  if (!ids.size) return
  const meta = readMeta(accountId)
  let changed = false
  for (const row of rows) {
    if (row.id == null) continue
    const id = String(row.id)
    if (!isUnread(row)) {
      if (ids.delete(id)) changed = true
      if (meta[id]) {
        delete meta[id]
        changed = true
      }
    } else if (ids.has(id) && !str(meta[id]?.type) && typeOf(row)) {
      meta[id] = { ...meta[id], type: typeOf(row), ts: Number(meta[id]?.ts) || Date.now() }
      changed = true
    }
  }
  if (changed) {
    writeIds(accountId, ids)
    writeMeta(accountId, meta)
  }
}

/** Rows this browser already marked read show as read. */
export function overlayRead(rows: Raw[], ids: Set<string>): Raw[] {
  if (!ids.size) return rows
  return rows.map((n) => (n.id != null && ids.has(String(n.id)) && isUnread(n) ? { ...n, is_read: true, isRead: true } : n))
}

export const markRows = (rows: Raw[], ids: Set<string>) => rows.map((n) => (n.id != null && ids.has(String(n.id)) && isUnread(n) ? { ...n, is_read: true, isRead: true } : n))

// ── Badge counts ────────────────────────────────────────────────────────────

export type Counts = Record<string, number>

const typeKeys = (t: string): string[] => {
  if (!t) return []
  if (t === 'subscribe' || t === 'subscribed') return ['subscribe', 'subscribed']
  if (t === 'purchase' || t === 'purchases') return ['purchase', 'purchases']
  if (t === 'comment' || t === 'commented') return ['comment', 'commented']
  if (t === 'mention' || t === 'mentioned') return ['mention', 'mentioned']
  return [t]
}

function dec(c: Counts, key: string, by = 1) {
  if (!key) return
  c[key] = Math.max(0, Math.max(0, Number(c[key]) || 0) - by)
}

/** `count` answer → per-type counts, minus what this browser already read. */
export function countsFrom(data: unknown, accountId: string): Counts {
  const d = obj(data)
  const payload = obj(d.data ?? d)
  const base = obj(payload.counts ?? payload.types ?? payload.categories ?? payload)
  const counts: Counts = {}
  for (const [k, v] of Object.entries(base)) if (typeof v === 'number' || (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v)))) counts[k] = Number(v)
  const ids = readIds(accountId)
  if (!ids.size) return counts
  const meta = readMeta(accountId)
  for (const id of ids) {
    dec(counts, 'all')
    dec(counts, 'total')
    dec(counts, 'count')
    for (const k of typeKeys(str(meta[id]?.type).toLowerCase())) dec(counts, k)
  }
  return counts
}

/** Counts after rows were read here (before the server confirms). */
export function decrementCounts(counts: Counts, rows: Raw[]): Counts {
  if (!rows.length) return counts
  const c = { ...counts }
  for (const row of rows) {
    const t = typeOf(row)
    if (t) dec(c, t)
    if (t === 'subscribe' || t === 'subscribed') dec(c, 'subscribed')
    if (t === 'purchase' || t === 'purchases') dec(c, 'purchases')
    if (t === 'comment' || t === 'commented') dec(c, 'commented')
    if (t === 'mention' || t === 'mentioned') dec(c, 'mentioned')
  }
  dec(c, 'all', rows.length)
  dec(c, 'total', rows.length)
  dec(c, 'count', rows.length)
  return c
}

/** A tab's badge ("" when nothing unread). */
export function badgeOf(counts: Counts, tab: TabId): string {
  let n: number
  if (tab === 'all') n = Number(counts.all ?? counts.total ?? counts.count ?? 0)
  else {
    const key = TAB_CONFIG[tab].apiType || tab
    n = Number(counts[key] ?? counts[tab] ?? counts[`${tab}s`] ?? 0)
  }
  if (!(n > 0)) return ''
  return n > 99 ? '99+' : String(n)
}

function obj(v: unknown): Raw {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Raw) : {}
}
